import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Radio, Users, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fetchChantingIndex, fetchChantSync } from "@/lib/chanting/chanting-service";

function formatCountdown(ms) {
  if (ms <= 0) return "Live now";
  const s = Math.ceil(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

/**
 * Phase 7 — Prayer Hall entry: list active global chants (schema-driven).
 */
export function GlobalChantingEntry({ compact = false }) {
  const [chants, setChants] = useState([]);
  const [live, setLive] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChantingIndex()
      .then(async (idx) => {
        const list = idx.chants || [];
        setChants(list);
        const syncs = {};
        await Promise.all(
          list.map(async (c) => {
            try {
              syncs[c.id] = await fetchChantSync(c.id);
            } catch {
              syncs[c.id] = null;
            }
          })
        );
        setLive(syncs);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <section data-testid="global-chanting-entry" className={compact ? "" : "mb-10"}>
      <div className="flex items-center gap-2 mb-4">
        <Radio className="w-5 h-5 text-primary animate-pulse" />
        <h2 className="font-heading text-xl">Global Chanting (LIVE)</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Sanskrit audio shared worldwide. Meaning slides in your language. Join mid-cycle — perfectly synced.
      </p>
      {error && <p className="text-destructive text-sm mb-3">{error}</p>}
      <div className="grid gap-3">
        {chants.map((c) => {
          const sync = live[c.id];
          const globalCount = sync?.global_stats?.total_cycles_completed_today ?? 0;
          const participants = sync?.active_participants ?? sync?.global_stats?.total_participations_today ?? 0;
          const msLeft = sync?.ms_until_cycle_end ?? 0;
          const inGap = sync?.cycle?.in_gap;
          return (
            <Card key={c.id} className="bg-white/90">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading">{c.title}</CardTitle>
                <p className="text-xs text-muted-foreground capitalize">
                  {c.schedule_mode?.replace("_", " ")} · {Math.round(c.duration_seconds / 60)} min cycle
                </p>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground space-y-1">
                  <p className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {inGap ? `Next wave: ${formatCountdown(msLeft)}` : "Join now — in progress"}
                  </p>
                  <p className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {participants} chanting now · {globalCount.toLocaleString()} cycles completed today
                  </p>
                </div>
                <Button size="sm" asChild>
                  <Link to={`/global-chanting/${c.id}`}>Join Now</Link>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
}

export function GlobalChantingPage() {
  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="global-chanting-page">
      <div className="max-w-2xl mx-auto">
        <GlobalChantingEntry />
        <div className="text-center mt-6 flex flex-wrap justify-center gap-2">
          <Button variant="default" asChild>
            <Link to="/community-hall">Community Hall (LIVE)</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/townhall">VedaPath Townhall</Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/prayer-hall">← Dhyāna Mandir (Prayer Hall)</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
